import React, { useCallback, useEffect, useState } from 'react';
import { FileText, Plus, X, MapPin, Calendar, CheckCircle2, Ruler, ClipboardList } from 'lucide-react';
import { format } from 'date-fns';
import {
  getFieldQuotesForBooking,
  resolveCandidateFieldQuotes,
  linkFieldQuotesToBooking,
  unlinkFieldQuoteFromBooking,
  type LinkedFieldQuote,
  type FieldQuoteCandidate,
} from '../../lib/bookings';
import { getFieldQuoteMeasurements, type ProjectMeasurement } from '../../lib/projectMeasurements';
import MeasurementItem from '../MeasurementItem';

interface BookingFieldQuotesSectionProps {
  bookingId: string;
  businessId: string;
  customerId?: string | null;
  opportunityId?: number | null;
  projectId?: string | null;
  canEdit: boolean;
  isMobile?: boolean;
  /** Called whenever the set of linked field quotes may have changed. */
  onChange?: () => void;
  /** Hide the outer card chrome (used when rendered inside a dedicated tab). */
  hideChrome?: boolean;
}

const MATCH_LABEL: Record<FieldQuoteCandidate['matched_by'], string> = {
  proposal: 'From proposal',
  opportunity: 'Same opportunity',
  project: 'Same project',
  customer: 'Same customer',
};

function formatDate(date: string | null): string {
  if (!date) return 'No date set';
  try {
    return format(new Date(date), 'MMM d, yyyy');
  } catch {
    return date;
  }
}

function formatTime(time: string | null): string | null {
  if (!time) return null;
  try {
    return format(new Date(`2000-01-01T${time}`), 'h:mm a');
  } catch {
    return time;
  }
}

function formatDateTime(date: string | null, time: string | null): string {
  if (!date) return 'Not scheduled';
  const t = formatTime(time);
  return t ? `${formatDate(date)} · ${t}` : formatDate(date);
}

/** Format a full timestamp (e.g. updated_at) as date + time. */
function formatTimestamp(ts: string | null): string {
  if (!ts) return '—';
  try {
    return format(new Date(ts), 'MMM d, yyyy · h:mm a');
  } catch {
    return ts;
  }
}

function resourceName(r: { first_name: string | null; last_name: string | null }): string {
  return `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || 'Technician';
}

function resourceInitials(r: { first_name: string | null; last_name: string | null }): string {
  return `${(r.first_name ?? '').charAt(0)}${(r.last_name ?? '').charAt(0)}`.toUpperCase() || '?';
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

function statusBadgeClasses(status: string): string {
  switch (status) {
    case 'completed':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'in_progress':
      return 'text-yellow-700 bg-yellow-50 border-yellow-200';
    case 'cancelled':
      return 'text-red-700 bg-red-50 border-red-200';
    default:
      return 'text-blue-700 bg-blue-50 border-blue-200';
  }
}

/** A single linked field quote rendered as a full container with its data,
 *  measurements, and technician notes. */
function LinkedFieldQuoteCard({
  fq,
  businessId,
  canEdit,
  isMobile,
  busy,
  onUnlink,
}: {
  fq: LinkedFieldQuote;
  businessId: string;
  canEdit: boolean;
  isMobile?: boolean;
  busy: boolean;
  onUnlink: () => void;
}) {
  const [measurements, setMeasurements] = useState<ProjectMeasurement[]>([]);
  const [loadingMeasurements, setLoadingMeasurements] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingMeasurements(true);
      try {
        const rows = await getFieldQuoteMeasurements(fq.id);
        if (!cancelled) setMeasurements(rows);
      } catch {
        if (!cancelled) setMeasurements([]);
      } finally {
        if (!cancelled) setLoadingMeasurements(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fq.id]);

  const completedAt = fq.status === 'completed' ? fq.updated_at : null;

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/80 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3 bg-gray-50/60">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">
              {fq.project_title || 'Field quote'}
            </span>
            <span className={`text-[11px] font-medium capitalize border rounded-full px-2 py-0.5 ${statusBadgeClasses(fq.status)}`}>
              {statusLabel(fq.status)}
            </span>
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={onUnlink}
            disabled={busy}
            className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Unlink field quote"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Key data */}
      <div className={`px-4 py-3 grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Scheduled</div>
          <div className="flex items-center gap-1.5 text-sm text-gray-800">
            <Calendar className="w-3.5 h-3.5 text-gray-400" />
            {formatDateTime(fq.start_date, fq.start_time)}
          </div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Completed</div>
          <div className="flex items-center gap-1.5 text-sm text-gray-800">
            <CheckCircle2 className={`w-3.5 h-3.5 ${completedAt ? 'text-green-500' : 'text-gray-300'}`} />
            {completedAt ? formatTimestamp(completedAt) : 'Not completed'}
          </div>
        </div>
        <div className="col-span-full">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Assigned</div>
          {fq.resources.length === 0 ? (
            <div className="text-sm text-gray-400">No one assigned</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {fq.resources.map((r) => (
                <div
                  key={r.id}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 border border-gray-200 pl-1 pr-2.5 py-0.5"
                >
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  ) : (
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[9px] font-semibold flex items-center justify-center">
                      {resourceInitials(r)}
                    </span>
                  )}
                  <span className="text-xs font-medium text-gray-700">{resourceName(r)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        {fq.field_quote_address && (
          <div className="col-span-full">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Address</div>
            <div className="flex items-start gap-1.5 text-sm text-gray-800">
              <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
              <span className="break-words">{fq.field_quote_address}</span>
            </div>
          </div>
        )}
      </div>

      {/* Measurements */}
      <div className="border-t border-gray-100">
        <div className="px-4 pt-3 pb-1 flex items-center gap-2">
          <Ruler className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[12px] font-semibold text-gray-700">
            Measurements{measurements.length > 0 ? ` (${measurements.length})` : ''}
          </span>
        </div>
        {loadingMeasurements ? (
          <div className="px-4 pb-3 text-sm text-gray-400">Loading…</div>
        ) : measurements.length === 0 ? (
          <div className="px-4 pb-3 text-sm text-gray-400">No measurements recorded.</div>
        ) : (
          <div className="pb-2 divide-y divide-gray-100">
            {measurements.map((m) => (
              <MeasurementItem
                key={m.id}
                measurement={m}
                businessId={businessId}
                canEdit={false}
                onEdit={() => {}}
                onDelete={() => {}}
              />
            ))}
          </div>
        )}
      </div>

      {/* Technician notes */}
      <div className="border-t border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[12px] font-semibold text-gray-700">Technician Notes</span>
        </div>
        {fq.technician_notes ? (
          <p className="text-sm text-gray-800 whitespace-pre-wrap">{fq.technician_notes}</p>
        ) : (
          <p className="text-sm text-gray-400">No technician notes.</p>
        )}
      </div>
    </div>
  );
}

export default function BookingFieldQuotesSection({
  bookingId,
  businessId,
  customerId,
  opportunityId,
  projectId,
  canEdit,
  isMobile,
  onChange,
  hideChrome,
}: BookingFieldQuotesSectionProps) {
  const [linked, setLinked] = useState<LinkedFieldQuote[]>([]);
  const [candidates, setCandidates] = useState<FieldQuoteCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const linkedRows = await getFieldQuotesForBooking(bookingId);
      setLinked(linkedRows);

      if (canEdit) {
        const linkedIds = new Set(linkedRows.map((r) => r.id));
        const cands = await resolveCandidateFieldQuotes({
          businessId,
          opportunityId: opportunityId ?? null,
          projectId: projectId ?? null,
          customerId: customerId ?? null,
        });
        setCandidates(cands.filter((c) => !linkedIds.has(c.id)));
      } else {
        setCandidates([]);
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId, businessId, customerId, opportunityId, projectId, canEdit]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleLink = async (fieldQuoteId: string) => {
    setBusyId(fieldQuoteId);
    try {
      await linkFieldQuotesToBooking(bookingId, [fieldQuoteId], businessId);
      await load();
      onChange?.();
    } finally {
      setBusyId(null);
    }
  };

  const handleUnlink = async (fieldQuoteId: string) => {
    setBusyId(fieldQuoteId);
    try {
      await unlinkFieldQuoteFromBooking(bookingId, fieldQuoteId);
      await load();
      onChange?.();
    } finally {
      setBusyId(null);
    }
  };

  // Hide the whole section when there's nothing to show and nothing to suggest.
  // (When chrome is hidden — i.e. inside the dedicated tab — keep rendering so the
  // loading/empty state is visible.)
  if (!hideChrome && !loading && linked.length === 0 && candidates.length === 0) {
    return null;
  }

  const pad = isMobile ? 'px-3 py-2.5' : 'px-6 py-4';

  const content = (
    <div className={hideChrome ? 'space-y-3' : `${pad} space-y-3`}>
      {loading ? (
        <div className="text-sm text-gray-400 py-2">Loading…</div>
      ) : (
        <>
          {linked.map((fq) => (
            <LinkedFieldQuoteCard
              key={fq.id}
              fq={fq}
              businessId={businessId}
              canEdit={canEdit}
              isMobile={isMobile}
              busy={busyId === fq.id}
              onUnlink={() => void handleUnlink(fq.id)}
            />
          ))}

          {canEdit && candidates.length > 0 && (
            <div className="pt-1">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                Suggested
              </div>
              <div className="space-y-2">
                {candidates.map((fq) => (
                  <div
                    key={fq.id}
                    className="flex items-center justify-between gap-3 rounded-[10px] border border-dashed border-gray-300 px-3 py-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {fq.project_title || 'Field quote'}
                        </span>
                        <span className="text-[11px] font-medium text-blue-600 bg-blue-50 rounded-full px-2 py-0.5 flex-shrink-0">
                          {MATCH_LABEL[fq.matched_by]}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(fq.start_date)}
                        </span>
                        {fq.field_quote_address && (
                          <span className="inline-flex items-center gap-1 truncate">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{fq.field_quote_address}</span>
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => void handleLink(fq.id)}
                      disabled={busyId === fq.id}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Link
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  if (hideChrome) {
    return content;
  }

  return (
    <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
      <div className={`${pad} border-b border-gray-100 flex items-center gap-2`}>
        <FileText className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-semibold text-gray-900">Field Quotes</span>
        {linked.length > 0 && (
          <span className="text-xs text-gray-400">({linked.length})</span>
        )}
      </div>
      {content}
    </div>
  );
}
